import { Download, Share, X } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'

import waveMark from '@/assets/brand/wave-mark@96.png'
import { Button } from '@/components/ui'
import { usePwaInstall } from '@/hooks/usePwaInstall'
import { cn } from '@/utils/cn'

/** Rotas onde o aviso de instalação pode aparecer. */
const ALLOWED_PATHS = ['/login', '/']

/**
 * Card flutuante que convida a instalar o PWA. Aparece só no login e na home,
 * some quando o app já está instalado ou o usuário fecha o aviso.
 */
export function InstallPrompt() {
  const { pathname } = useLocation()
  const { visible, mode, promptInstall, dismiss } = usePwaInstall()

  if (typeof document === 'undefined') return null
  if (!ALLOWED_PATHS.includes(pathname) || !visible) return null

  // Na home a MobileNav ocupa a base até o breakpoint lg; o card fica logo acima
  // dela. No login não há nav: fica rente à base, com uma folga pequena.
  // O inset soma a área do indicador de gestos do iPhone (0 nos demais).
  const bottomOffset =
    pathname === '/'
      ? 'bottom-[calc(5rem+env(safe-area-inset-bottom))] lg:bottom-4'
      : 'bottom-[calc(1rem+env(safe-area-inset-bottom))]'

  return createPortal(
    <div
      role="region"
      aria-label="Instalar aplicativo"
      className={cn(
        'pc-animate-sheet fixed inset-x-4 z-30 sm:inset-x-auto sm:right-4 sm:w-80',
        bottomOffset,
      )}
    >
      <div className="pc-card relative flex gap-3 p-4 shadow-lg">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar"
          className="absolute right-2 top-2 text-fg-subtle transition-colors hover:text-fg"
        >
          <X className="size-4" />
        </button>

        <img src={waveMark} alt="" aria-hidden className="h-9 w-auto shrink-0" />

        <div className="min-w-0 flex-1 pr-4">
          <p className="font-semibold text-fg">Instale o PachecoControl</p>

          {mode === 'native' ? (
            <>
              <p className="mt-1 text-sm text-fg-muted">
                Acesse rápido pela tela inicial do seu aparelho, como um aplicativo.
              </p>
              <Button size="sm" onClick={promptInstall} className="mt-3">
                <Download className="size-4" />
                Instalar
              </Button>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm text-fg-muted">
                Toque em{' '}
                <Share
                  className="mx-0.5 inline size-4 align-middle text-brand"
                  aria-label="Compartilhar"
                />{' '}
                e depois em{' '}
                <span className="font-medium text-fg">“Adicionar à Tela de Início”</span>.
              </p>
              <Button variant="ghost" size="sm" onClick={dismiss} className="mt-3">
                Entendi
              </Button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
